/**
 * Safe HTTP helpers — read response body exactly once.
 *
 * The browser Response API only allows the body to be consumed once.
 * Calling `.json()` (or `.text()`) twice — directly OR via different code
 * paths — throws "Failed to execute 'json' on 'Response': body stream
 * already read". This module makes that bug structurally impossible.
 *
 * Use `readBody(response)` in place of `await response.json()` everywhere.
 * It always returns an object (never throws), parses as JSON when possible,
 * and falls back to a `{detail: text}` shape so callers can do the same
 * `.detail` lookup on errors as they would on parsed JSON.
 */

export async function readBody(response) {
  try {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch (e) {
      return { detail: text.slice(0, 500) };
    }
  } catch (e) {
    // .text() threw — connection dropped mid-response. Surface the actual
    // HTTP status (when present) so callers don't show a generic "Network
    // connection dropped" toast for what may be a clean 4xx with truncated
    // body (CDN/edge proxies can do this).
    const status = response && typeof response.status === 'number' ? response.status : 0;
    const statusHint = status > 0 ? ` (HTTP ${status})` : '';
    return {
      _readBodyFailed: true,
      _httpStatus: status,
      detail: `Network connection dropped while reading the server response${statusHint}. Please try again.`,
    };
  }
}

/**
 * Status-based default error messages.
 *
 * Used when the response body could not be read (CDN truncation, mid-stream
 * disconnect) but the HTTP status itself is clear. The previous behaviour
 * was to keep retrying until all attempts were exhausted and then surface
 * "Server unreachable" — which incorrectly diagnosed a clean 401 as a
 * network outage.
 *
 * Specific endpoints (login, etc.) override with their own copy in the
 * caller; this is the safety net.
 */
function defaultMessageForStatus(status) {
  if (status === 401) return 'Incorrect email or password.';
  if (status === 403) return 'You do not have access to this action.';
  if (status === 404) return 'Not found.';
  if (status === 422) return 'Some details look invalid. Please check the form and try again.';
  if (status === 423) return 'Account temporarily locked due to multiple unsuccessful login attempts. Please wait a few minutes or contact an administrator.';
  if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
  if (status >= 500 && status < 600) return 'Service temporarily unavailable. Please try again.';
  if (status >= 400 && status < 500) return 'Request was rejected. Please refresh and try again.';
  return 'Server unreachable. Please check your connection and try again — or contact support if this persists.';
}

/**
 * fetchWithReadRetry — wraps fetch + readBody and retries up to 4 times if
 * the body read fails. Most common cause: Cloudflare/CDN cutting the response
 * body mid-stream during burst load. Returns { ok, status, body }.
 *
 * Retry schedule: immediate, then 1.5s, 4s, 8s. Total worst case ~14s.
 *
 * Short-circuit rule: if body-read fails BUT we have a real 4xx HTTP status
 * from the response, do NOT retry — this was a clean client error (e.g. 401
 * Invalid password) and retrying simply masks it as "Server unreachable".
 * We retry only on 5xx and on status==0 (no response at all).
 */
export async function fetchWithReadRetry(url, options) {
  const backoffs = [0, 1500, 4000, 8000];
  const attempts = backoffs.length;
  let lastError = null;
  for (let attempt = 0; attempt < backoffs.length; attempt++) {
    if (backoffs[attempt] > 0) {
      await new Promise((r) => setTimeout(r, backoffs[attempt]));
    }
    let resp;
    try {
      resp = await fetch(url, options);
    } catch (err) {
      lastError = err;
      // eslint-disable-next-line no-console
      console.warn(`[fetchWithReadRetry] attempt ${attempt + 1} fetch threw:`, err.message || err);
      continue;
    }
    const body = await readBody(resp);
    if (!body._readBodyFailed) {
      return { ok: resp.ok, status: resp.status, body };
    }
    // Body-read failed but we DO have a response — surface 4xx immediately.
    const status = body._httpStatus || resp.status || 0;
    if (status >= 400 && status < 500) {
      return {
        ok: false,
        status,
        body: { detail: defaultMessageForStatus(status) },
      };
    }
    // eslint-disable-next-line no-console
    console.warn(`[fetchWithReadRetry] attempt ${attempt + 1} body-read failed (status=${status})`);
  }
  // All retries exhausted. Distinguish "no response at all" from "response
  // arrived but unreadable" with a status hint.
  return {
    ok: false,
    status: 0,
    body: {
      detail: lastError
        ? 'Service temporarily unavailable. Please check your connection and try again.'
        : 'Service temporarily unavailable. Please try again in a moment.',
    },
  };
}
